import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { debounce } from 'lodash';
import { useAuth0 } from '@auth0/auth0-react';

interface UseChildFilterProps {
  searchApiFunction: (params: { query: string; ids?: string[] }, auth0: any) => Promise<any[]>;
  initialSelectedIds?: string[];
  debounceMs?: number;
  loadInitialData?: boolean; // New prop to control initial data loading
}

export const useChildFilter = ({
  searchApiFunction,
  initialSelectedIds = [],
  debounceMs = 500, // Increased default from 300ms to 500ms
  loadInitialData = false
}: UseChildFilterProps) => {
  const auth0 = useAuth0();
  const [fetchedOptions, setFetchedOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [checkedItems, setCheckedItems] = useState<string[]>(() => {
    return initialSelectedIds;
  });
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Track current search query to prevent race conditions
  const currentSearchQueryRef = useRef<string>('');

  // Create a stable search function - can handle both empty and non-empty queries
  const performSearch = useCallback((search: string, currentCheckedItems: string[] = []) => {
    // Update ref with current query
    currentSearchQueryRef.current = search;
    
    setLoading(true);
    setError(null);
    
    searchApiFunction({ query: search, ids: currentCheckedItems }, auth0)
      .then((results) => {
        // CRITICAL: Only update state if this is still the latest search query
        // This prevents stale responses from overwriting correct results
        if (currentSearchQueryRef.current === search) {
          // Filter out any undefined/null results
          const validResults = (results || []).filter((item: any) => item && (item._id || item.id));
          setLoading(false);
          setFetchedOptions(validResults);
        }
      })
      .catch((e) => {
        // Only update state if this is still the latest request
        if (currentSearchQueryRef.current === search) {
          setLoading(false);
          setError('Failed to fetch options');
        }
      });
  }, [searchApiFunction, auth0]);

  // Create debounced search function using useMemo to ensure it's stable
  // Update debounced function when debounceMs changes
  const debouncedSearch = useMemo(
    () =>
      debounce((search: string, currentCheckedItems: string[]) => {
        performSearch(search, currentCheckedItems);
      }, debounceMs),
    [debounceMs, performSearch]
  );

  // Handle search input change
  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchQuery(query);
      // Only trigger search if there's a non-empty query
      if (query.trim()) {
        debouncedSearch(query, checkedItems);
      } else {
        // Clear results immediately for empty query
        setFetchedOptions([]);
        setLoading(false);
      }
    },
    [debouncedSearch]
  );

  // Handle checkbox selection change
  const handleSelectionChange = useCallback((selectedIds: string[]) => {
    
    setCheckedItems(selectedIds);
  }, []);

  // Get selected options
  const getSelectedOptions = useCallback(() => {
    return fetchedOptions.filter(option => checkedItems.includes(option._id));
  }, [fetchedOptions, checkedItems]);

  // Clear all selections
  const clearSelections = useCallback(() => {
    setCheckedItems([]);
    setSearchQuery('');
    setError(null);
    setLoading(false);
  }, []);

  // Reset to initial state
  const reset = useCallback(() => {
    setCheckedItems(initialSelectedIds);
    setSearchQuery('');
    setError(null);
    setFetchedOptions([]); // Clear options instead of searching
  }, [initialSelectedIds]);

  // Initial load - only when loadInitialData is true and filter is selected
  useEffect(() => {
    if (loadInitialData && !initialized && auth0.isAuthenticated) {
      setInitialized(true);
      performSearch('', initialSelectedIds); // Load initial data with empty query
    }
  }, [loadInitialData, initialized, auth0.isAuthenticated, initialSelectedIds, performSearch]);


  return {
    // State
    fetchedOptions,
    loading,
    searchQuery,
    checkedItems,
    error,
    
    // Actions
    handleSearchChange,
    handleSelectionChange,
    getSelectedOptions,
    clearSelections,
    reset,
    
    // Computed
    hasSelections: checkedItems.length > 0,
    selectedCount: checkedItems.length,
  };
};

export default useChildFilter;
